import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useCallback, useEffect } from "react";
import { DateTime } from "luxon";
import { formatDuration } from "../../core/timesheet/utils/utils";
import { getTimesheets } from "core/timesheet";
import { PTORequest, Timesheet } from "@otw/models";
import { useAuth } from "hooks/auth/useAuth";
import { addDoc, doc, getDoc, updateDoc, writeBatch, collection, deleteDoc, runTransaction } from "firebase/firestore";
import { db } from "@/config/firebase";
import { db as firebaseDb } from "config/firebase";

export const useTimesheet = () => {
  const queryClient = useQueryClient();
  const [activeTimerTimesheet, setActiveTimerTimesheet] = useState<
    Timesheet | undefined
  >(undefined);
  const [timerStartTime, setTimerStartTime] = useState<string | null>(null);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [timerValue, setTimerValue] = useState("00:00:00");
  const [isSaving, setIsSaving] = useState(false);
  const { currentUser, userInfo } = useAuth();

  const userId = userInfo?.id;

  if (!currentUser || !userId) {
    throw new Error("No authenticated user");
  }

  const {
    data: timesheets = [],
    isLoading: isLoadingTimesheets,
    error: timesheetsError,
    refetch: refetchTimesheets,
  } = useQuery({
    queryKey: ["timesheets", userId],
    queryFn: async () => {
      // Simplified version that doesn't require complex dependencies
      return getTimesheets({
        userId,
        clients: [],
        projects: [],
        projectAssignments: [],
      });
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  const createTimesheetMutation = useMutation({
    mutationFn: async (timesheetData: Omit<Timesheet, "id">) => {
      const timesheetWithUser: Omit<Timesheet, "id"> = {
        ...timesheetData,
        userId: currentUser.uid,
      };
      const timesheetsRef = collection(db, "timesheets");
      const docRef = await addDoc(timesheetsRef, timesheetWithUser);
      return docRef.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["timesheets", userId] });
    },
  });

  type SubmitPTORequestInput = {
    startDate: string;
    endDate: string;
    daysRequested: number;
    reason?: string;
  };

  const submitPTORequest = useMutation({
    mutationFn: async (ptoData: SubmitPTORequestInput) => {
      const now = DateTime.now().toISO();
      const trimmedReason = ptoData.reason?.trim();
      const ptoRequest: Omit<PTORequest, "id"> = {
        userId,
        startDate: ptoData.startDate,
        endDate: ptoData.endDate,
        daysRequested: ptoData.daysRequested,
        status: "pending",
        createdAt: now,
        updatedAt: now,
        ...(trimmedReason ? { reason: trimmedReason } : {}),
      };

      const ptoRequestsRef = collection(db, "pto_requests");
      const docRef = await addDoc(ptoRequestsRef, ptoRequest);
      return docRef.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pto_requests", userId] });
    },
  });

  const deletePTORequest = useMutation({
    mutationFn: async (id: string) => {
      const requestRef = doc(db, "pto_requests", id);
      await deleteDoc(requestRef);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pto_requests", userId] });
    },
  });

  const updateTimesheetMutation = useMutation({
    mutationFn: async (timesheetData: Partial<Timesheet> & { id: string }) => {
      const timesheetRef = doc(db, "timesheets", timesheetData.id);
      await updateDoc(timesheetRef, timesheetData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["timesheets", userId] });
    },
  });

  const deleteTimesheetMutation = useMutation({
    mutationFn: async (id: string) => {
      const timesheetRef = doc(db, "timesheets", id);
      await deleteDoc(timesheetRef);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["timesheets", userId] });
    },
  });

  const bulkDeleteTimesheetsMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const batch = writeBatch(firebaseDb);
      ids.forEach((id) => {
        const timesheetRef = doc(db, "timesheets", id);
        batch.delete(timesheetRef);
      });
      await batch.commit();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["timesheets", userId] });
    },
  });

  const fetchTimerState = async (timesheetId: string) => {
    if (!timesheetId) return null;
    const timesheetRef = doc(db, "timesheets", timesheetId);
    const timesheet = await getDoc(timesheetRef);
    return timesheet.data();
  };

  const { data: firestoreTimerState, refetch: refetchTimerState } = useQuery({
    queryKey: ["timerState", activeTimerTimesheet?.id],
    queryFn: () => fetchTimerState(activeTimerTimesheet?.id || ""),
    enabled: !!activeTimerTimesheet?.id,
    staleTime: 5 * 60 * 1000,
  });

  const startTimer = useCallback(
    async (newTimesheet: Omit<Timesheet, "id">) => {
      const userRef = doc(db, "users", userId);
      try {
        // Prevent duplicate active timesheets: if one already exists (this tab, double-click, or another tab), resume it instead of creating
        const existingActive = timesheets.find((t) => t.timerActive);
        if (existingActive) {
          setActiveTimerTimesheet(existingActive);
          setIsTimerActive(true);
          setTimerStartTime(existingActive.startTime);
          await updateDoc(userRef, { activeTimerId: existingActive.id });
          return;
        }

        // Optional: check user doc for activeTimerId (covers multi-tab before query has synced)
        const userSnap = await getDoc(userRef);
        const activeTimerId = userSnap.data()?.activeTimerId as string | undefined;
        if (activeTimerId) {
          const existingRef = doc(db, "timesheets", activeTimerId);
          const existingSnap = await getDoc(existingRef);
          const data = existingSnap.data();
          if (existingSnap.exists() && data?.timerActive) {
            const existing: Timesheet = { ...data, id: existingSnap.id } as Timesheet;
            setActiveTimerTimesheet(existing);
            setIsTimerActive(true);
            setTimerStartTime(existing.startTime);
            return;
          }
        }

        const now = DateTime.now();

        const createdTimesheetId = await createTimesheetMutation.mutateAsync({
          ...newTimesheet,
          startTime: now.toISO(),
          duration: 0,
          userId,
          timerActive: true,
        });

        const createdTimesheet: Timesheet = {
          ...newTimesheet,
          id: createdTimesheetId,
          startTime: now.toISO(),
          duration: 0,
          timerActive: true,
        };

        setActiveTimerTimesheet(createdTimesheet);
        setIsTimerActive(true);
        setTimerStartTime(now.toISO());

        await updateDoc(userRef, { activeTimerId: createdTimesheetId });
      } catch (error) {
        console.error("Error starting timer:", error);
        throw error;
      }
    },
    [createTimesheetMutation, userId, timesheets]
  );

  const fetchActiveTimer = useCallback(async () => {
    // Find active timer from existing timesheets data
    const activeTimesheet = timesheets.find(entry => entry.timerActive);
    
    if (activeTimesheet) {
      setActiveTimerTimesheet(activeTimesheet);
      setIsTimerActive(true);
      setTimerStartTime(activeTimesheet.startTime);
      
      // Calculate current duration from start time
      const startTime = DateTime.fromISO(activeTimesheet.startTime);
      const now = DateTime.now();
      const currentDuration = Math.round(now.diff(startTime).as("seconds"));
      
      // Update the duration in the database to reflect current time
      await updateTimesheetMutation.mutateAsync({
        id: activeTimesheet.id,
        duration: currentDuration,
      });
    }
  }, [timesheets, updateTimesheetMutation]);

  const stopTimer = useCallback(async () => {
    setIsSaving(true);
    try {
      console.log("stopTimer called, activeTimerTimesheet:", activeTimerTimesheet);
      console.log("timesheets data:", timesheets);
      
      // Find the active timesheet to stop - either from state or from data
      let timesheetToStop = activeTimerTimesheet;
      if (!timesheetToStop) {
        // Fallback: find active timer from timesheets data
        timesheetToStop = timesheets.find(entry => entry.timerActive);
        console.log("Fallback: found active timer from data:", timesheetToStop);
      }

      if (timesheetToStop) {
        console.log("Stopping timesheet:", timesheetToStop.id);
        const timesheetRef = doc(db, "timesheets", timesheetToStop.id);
        const endTime = DateTime.now();

        const stopResult = await runTransaction(db, async (transaction) => {
          const snap = await transaction.get(timesheetRef);
          if (!snap.exists()) {
            return { updated: false, reason: "missing" as const };
          }

          const latest = snap.data() as Partial<Timesheet>;
          if (latest.timerActive !== true || !!latest.endTime) {
            return { updated: false, reason: "already_stopped" as const };
          }

          const latestStartTime =
            typeof latest.startTime === "string" ? latest.startTime : timerStartTime;
          if (!latestStartTime) {
            throw new Error("Start time is missing on the active timesheet");
          }

          const start = DateTime.fromISO(latestStartTime);
          if (!start.isValid) {
            throw new Error("Start time is invalid on the active timesheet");
          }

          const duration = Math.max(0, Math.round(endTime.diff(start).as("seconds")));
          transaction.update(timesheetRef, {
            endTime: endTime.toISO(),
            duration,
            timerActive: false,
          });

          return { updated: true as const };
        });

        if (stopResult.updated) {
          console.log("Successfully stopped timer");
        } else {
          console.warn(
            `Skip stopTimer update for ${timesheetToStop.id}: ${stopResult.reason}`
          );
        }
      } else {
        console.error("No active timesheet found to stop");
        throw new Error("No active timesheet found to stop");
      }

      setIsTimerActive(false);
      setTimerStartTime(null);
      setActiveTimerTimesheet(undefined);
      setTimerValue("00:00:00");

      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, { activeTimerId: null });
      
      // Invalidate all related queries to ensure state is fresh
      queryClient.invalidateQueries({ queryKey: ["timesheets", userId] });
      queryClient.invalidateQueries({ queryKey: ["timerState"] });
      
      // Force refetch timesheets to ensure state is in sync
      await refetchTimesheets();
      await refetchTimerState();
      
      return true;
    } catch (error) {
      console.error("Error stopping timer:", error);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [
    activeTimerTimesheet,
    timesheets,
    timerStartTime,
    refetchTimerState,
    userId,
    refetchTimesheets,
    queryClient,
  ]);

  const updateTimerValue = useCallback(() => {
    if (timerStartTime) {
      const diff = DateTime.now().diff(
        DateTime.fromISO(timerStartTime),
        "seconds"
      );
      const newTimerValue = formatDuration(Math.floor(diff.seconds));
      setTimerValue(newTimerValue);
    }
  }, [timerStartTime]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isTimerActive) {
      interval = setInterval(updateTimerValue, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerActive, updateTimerValue]);

  // Check for existing active timer only on page load
  useEffect(() => {
    if (timesheets && timesheets.length > 0 && !isSaving) {
      const activeEntry = timesheets.find(entry => entry.timerActive);
      
      if (activeEntry && !isTimerActive) {
        // Found an active timer that we weren't tracking
        setActiveTimerTimesheet(activeEntry);
        setIsTimerActive(true);
        setTimerStartTime(activeEntry.startTime);
      } else if (!activeEntry && isTimerActive) {
        // No active timer in database but we think we have one
        setIsTimerActive(false);
        setTimerStartTime(null);
        setActiveTimerTimesheet(undefined);
        setTimerValue("00:00:00");
      }
    }
  }, [timesheets, isSaving]); // Removed isTimerActive from dependencies to prevent loops

  // Monitor activeTimerTimesheet changes and sync state
  useEffect(() => {
    if (activeTimerTimesheet) {
      setIsTimerActive(true);
      setTimerStartTime(activeTimerTimesheet.startTime);
    } else {
      setIsTimerActive(false);
      setTimerStartTime(null);
      setTimerValue("00:00:00");
    }
  }, [activeTimerTimesheet]);

  return {
    timesheets: timesheets as Timesheet[],
    timesheetsError,
    isLoadingTimesheets,
    refetchTimesheets,
    createTimesheet: createTimesheetMutation,
    updateTimesheet: updateTimesheetMutation,
    deleteTimesheet: deleteTimesheetMutation,
    bulkDeleteTimesheets: bulkDeleteTimesheetsMutation,
    activeTimerTimesheet,
    startTimer,
    stopTimer,
    refetchTimerState,
    submitPTORequest,
    deletePTORequest,
    updateTimerValue,
    isTimerActive,
    timerValue,
    isSaving,
    isStarting: createTimesheetMutation.isPending,
    setTimerValue,
    fetchActiveTimer,
  };
};

